<template>
  <div class="w-full mt-2" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="space-y-3">
      <button
        v-for="option in sourceOptions"
        :key="option.id"
        type="button"
        @click="onSourceOptionClick(option.id)"
        class="w-full p-4 border rounded-2xl transition-colors text-left shadow-sm hover:shadow-md bg-white/90 dark:bg-stone-900/70 border-stone-200 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800/70"
        :style="isRtl === true ? { textAlign: 'right' } : { textAlign: 'left' }"
      >
        <div class="flex items-start" :class="isRtl === true ? 'flex-row-reverse' : 'flex-row'">
          <span class="w-10 h-10 rounded-xl bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 flex items-center justify-center flex-shrink-0" :class="isRtl === true ? 'ml-3' : 'mr-3'">
            <Icon :icon="option.icon" class="text-stone-500 dark:text-stone-300" width="20" />
          </span>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-slate-800 dark:text-stone-50">{{ option.title }}</div>
            <div class="text-sm text-slate-600 dark:text-slate-400 mt-1 leading-6">{{ option.description }}</div>
          </div>
        </div>
      </button>
    </div>
    <div v-if="sources.length > 0" class="overflow-x-auto rounded-xl border border-stone-200 dark:border-stone-700 mt-4">
      <table class="table table-sm w-full">
        <thead>
          <tr class="bg-stone-100/80 dark:bg-stone-900/80 text-slate-700 dark:text-slate-300">
            <th>{{ t('agentCreate.knowledge.sources.table.type', 'Type') }}</th>
            <th>{{ t('agentCreate.knowledge.sources.table.name', 'Source') }}</th>
            <th>{{ t('agentCreate.knowledge.sources.table.detail', 'Details') }}</th>
            <th class="w-12"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="source in sources" :key="source.id" class="border-t border-stone-100 dark:border-stone-800">
            <td>
              <span class="inline-flex items-center text-sm">
                <Icon :icon="resolveTypeIcon(source.type)" width="16" :class="isRtl === true ? 'ml-1.5' : 'mr-1.5'" class="text-stone-500 dark:text-stone-300" />
                {{ resolveTypeLabel(source.type) }}
              </span>
            </td>
            <td class="max-w-[200px] truncate font-medium text-slate-800 dark:text-slate-200">{{ source.label }}</td>
            <td class="max-w-[240px] truncate text-sm text-slate-600 dark:text-slate-400">{{ source.detail }}</td>
            <td class="text-right">
              <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/80 text-slate-400 transition-colors hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/30"
                :title="t('common.remove', 'Remove')"
                @click="removeSource(source.id)"
              >
                <Icon icon="tabler:trash" width="16" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <AgentCreateKnowledgeWebsiteModal :visible="showWebsiteModal === true" @close="showWebsiteModal = false" @confirm="onWebsiteConfirm" />
    <AgentCreateKnowledgeUrlsModal :visible="showUrlsModal === true" @close="showUrlsModal = false" @confirm="onUrlsConfirm" />
    <AgentCreateKnowledgeFilesModal :visible="showFilesModal === true" @close="showFilesModal = false" @confirm="onFilesConfirm" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import AgentCreateKnowledgeWebsiteModal from '@/components/agentCreate/AgentCreateKnowledgeWebsiteModal.vue';
import AgentCreateKnowledgeUrlsModal from '@/components/agentCreate/AgentCreateKnowledgeUrlsModal.vue';
import AgentCreateKnowledgeFilesModal from '@/components/agentCreate/AgentCreateKnowledgeFilesModal.vue';

const emit = defineEmits(['sources-change']);

const { t, currentLanguage, availableLanguages } = useI18n();

const sources = ref([]);
const showWebsiteModal = ref(false);
const showUrlsModal = ref(false);
const showFilesModal = ref(false);
let sourceCounter = 0;

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

const sourceOptions = computed(() => [
  {
    id: 'website',
    title: t('agentCreate.knowledge.sources.website', 'Entire website'),
    description: t('agentCreate.knowledge.sources.websiteDescription', 'Crawl and index all pages from your main website URL.'),
    icon: 'tabler:world-www'
  },
  {
    id: 'urls',
    title: t('agentCreate.knowledge.sources.urls', 'Specific links'),
    description: t('agentCreate.knowledge.sources.urlsDescription', 'Add one URL per line for targeted pages only.'),
    icon: 'tabler:link'
  },
  {
    id: 'files',
    title: t('agentCreate.knowledge.sources.files', 'Documents'),
    description: t('agentCreate.knowledge.sources.filesDescription', 'Upload PDF, Word, text, and similar files.'),
    icon: 'tabler:file-upload'
  }
]);

function resolveTypeIcon(type) {
  if (type === 'website') {
    return 'tabler:world-www';
  }
  if (type === 'urls') {
    return 'tabler:link';
  }
  return 'tabler:file-upload';
}

function resolveTypeLabel(type) {
  if (type === 'website') {
    return t('agentCreate.knowledge.sources.typeWebsite', 'Website');
  }
  if (type === 'urls') {
    return t('agentCreate.knowledge.sources.typeUrls', 'Links');
  }
  return t('agentCreate.knowledge.sources.typeFiles', 'Documents');
}

function notifySourcesChange() {
  emit('sources-change', getSourcesSummary());
}

function onSourceOptionClick(optionId) {
  if (optionId === 'website') {
    showWebsiteModal.value = true;
    return;
  }
  if (optionId === 'urls') {
    showUrlsModal.value = true;
    return;
  }
  if (optionId === 'files') {
    showFilesModal.value = true;
  }
}

function addSource(source) {
  sourceCounter = sourceCounter + 1;
  sources.value = sources.value.concat([{
    id: `source-${sourceCounter}`,
    type: source.type,
    label: source.label,
    detail: source.detail,
    urls: source.urls || [],
    fileNames: source.fileNames || [],
    files: source.files || []
  }]);
  notifySourcesChange();
}

function onWebsiteConfirm(url) {
  showWebsiteModal.value = false;
  let displayUrl = url;
  if (url.length > 48) {
    displayUrl = url.substring(0, 45) + '...';
  }
  addSource({
    type: 'website',
    label: displayUrl,
    detail: url
  });
}

function onUrlsConfirm(urls) {
  showUrlsModal.value = false;
  const count = urls.length;
  const label = t('agentCreate.knowledge.sources.linksCount', '{count} links', { count });
  let detail = urls[0];
  if (count > 1) {
    detail = detail + ` (+${count - 1})`;
  }
  addSource({
    type: 'urls',
    label,
    detail,
    urls
  });
}

function onFilesConfirm(files) {
  showFilesModal.value = false;
  const sourceFiles = Array.isArray(files) === true ? files : [];
  const fileNames = [];
  let index = 0;
  while (index < sourceFiles.length) {
    const file = sourceFiles[index];
    const fileName = file !== undefined && file !== null && typeof file.name === 'string' ? file.name : '';
    if (fileName.length > 0) {
      fileNames.push(fileName);
    }
    index = index + 1;
  }
  if (fileNames.length === 0) {
    return;
  }
  const count = fileNames.length;
  let label = fileNames[0];
  if (count > 1) {
    label = t('agentCreate.knowledge.sources.filesCount', '{count} files', { count });
  }
  let detail = fileNames.join(', ');
  if (detail.length > 60) {
    detail = detail.substring(0, 57) + '...';
  }
  addSource({
    type: 'files',
    label,
    detail,
    fileNames,
    files: sourceFiles
  });
}

function removeSource(id) {
  sources.value = sources.value.filter((item) => item.id !== id);
  notifySourcesChange();
}

function getSourcesSummary() {
  if (sources.value.length === 0) {
    return '';
  }
  const countLabel = t('agentCreate.knowledge.sources.historyCount', '{count} sources', { count: sources.value.length });
  return countLabel;
}

function getSources() {
  return sources.value.slice();
}

function hasSources() {
  return sources.value.length > 0;
}

defineExpose({
  getSources,
  hasSources,
  getSourcesSummary
});
</script>
